/* PublicNewsRadar — Radar de Notícias dos Vetores LSC.
 *
 * Feed compacto exibido na home pública abaixo do mapa.
 *
 * Regra de clique (Fev/2026, sessão 30):
 *   - `original_url` → link DIRETO da matéria específica. ÚNICO campo usado
 *     para abrir o card e o link "Fonte ↗".
 *   - `source_url`   → página institucional da fonte (informativo, NUNCA
 *     usado para clique principal — abriria só a home do portal).
 *   - `source_name`  → nome amigável da fonte (ex: "Agência Brasil").
 *
 *   Card sem `original_url` → não clicável, mostra "Matéria indisponível".
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radio, ExternalLink, RefreshCw, AlertCircle, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const VETOR_COLORS = {
  GLO: "#0F4C8B",
  "Fronteiras": "#9B6D2A",
  "Segurança Marítima": "#1B7A8B",
  "Inteligência de Defesa": "#5C2D8A",
  "Apoio Interagências": "#506B30",
  "Defesa Civil": "#B0561C",
  "Grandes Eventos": "#7A1E3C",
  "Antiterrorismo": "#7A1E1E",
  "Ilícitos Ambientais": "#356F30",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diffH = Math.floor((Date.now() - then) / 3_600_000);
  if (diffH < 1) return "< 1h atrás";
  if (diffH < 24) return `${diffH}h atrás`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d atrás`;
}

/**
 * Resolve a URL clicável da notícia. Somente `original_url` — sem fallback.
 * Isso garante que o card NUNCA abra apenas a home da fonte.
 */
function getNewsUrl(n) {
  const url = n?.original_url;
  if (typeof url === "string" && /^https?:\/\//i.test(url)) return url;
  return null;
}

export default function PublicNewsRadar() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: payload } = await api.get("/public/news-radar", { params: { limit: 9 } });
      setData(payload);
    } catch (e) {
      console.warn("Radar load falhou:", e);
      setErr("Não foi possível carregar o feed agora.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <section
      className="bg-white border-y border-slate-200 py-10 sm:py-12"
      data-testid="public-news-radar"
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-7">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded bg-[#0F1B2D] text-[#C5A028] flex items-center justify-center">
                <Radio size={14} />
              </div>
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-500">
                Radar Público
              </span>
            </div>
            <h2 className="font-display font-bold text-[26px] sm:text-[28px] leading-tight text-[#0F1B2D] flex items-center gap-2">
              Radar de Notícias dos Vetores LSC
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Sobre o Radar de Notícias"
                    title="Sobre o Radar"
                    data-testid="news-radar-help"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 text-slate-400 hover:text-[#0F1B2D] hover:bg-slate-50 hover:border-slate-300 transition focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                  >
                    <Info size={13} />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" className="w-[320px] max-w-[90vw] p-4 text-[13px] leading-relaxed text-slate-600">
                  <div className="font-display font-semibold text-[#0F1B2D] text-sm mb-1.5">Radar de Notícias</div>
                  O Radar exibe notícias abertas classificadas pelos vetores do LSC. O feed
                  não representa validação metodológica nem aprovação como Semente de Futuro.
                </PopoverContent>
              </Popover>
            </h2>
            <p className="text-[15px] sm:text-base text-slate-600 mt-2 max-w-3xl leading-relaxed">
              Monitoramento de notícias abertas relacionadas aos vetores de pesquisa em
              Segurança Pública &amp; Defesa.
            </p>
            <p className="text-[13px] text-slate-500 mt-1.5 max-w-3xl leading-relaxed">
              Este radar apresenta notícias abertas classificadas pelos vetores de pesquisa do LSC.
              Em ambiente demonstrativo, alguns itens podem aparecer sem link de matéria.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data && (
              <span
                className="text-[11px] uppercase tracking-wide font-semibold px-2.5 py-1 bg-[#C5A028]/10 text-[#8C6F1A] border border-[#C5A028]/30 rounded"
                data-testid="news-source-badge"
                title="Origem dos dados deste feed"
              >
                {data.data_source_label || "Demonstração"}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="p-2 rounded border border-slate-200 text-slate-500 hover:text-[#0F1B2D] hover:bg-slate-50 disabled:opacity-50 transition"
              title="Atualizar feed"
              data-testid="news-refresh"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {err && (
          <div className="text-sm text-rose-600 flex items-center gap-2 py-4">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        {!err && (() => {
          // Vitrine pública: exibir SOMENTE cards com original_url válido.
          // Mock sem link e itens descartados não aparecem aqui (continuam
          // visíveis na área admin via ColetaPublica.jsx).
          const visibleItems = (data?.items || []).filter((n) => Boolean(getNewsUrl(n)));

          if (visibleItems.length === 0 && !loading) {
            return (
              <div
                className="bg-white border border-slate-200 rounded-lg p-8 sm:p-10 text-center"
                data-testid="news-empty-state"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 text-amber-600 mb-4">
                  <RefreshCw size={20} />
                </div>
                <h3 className="font-display font-semibold text-[#0F1B2D] text-base sm:text-lg">
                  Radar em atualização
                </h3>
                <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto leading-relaxed">
                  Novas notícias abertas serão exibidas após a próxima coleta.
                </p>
                <p className="text-xs text-slate-400 mt-3 max-w-md mx-auto italic leading-relaxed">
                  O feed público exibe apenas notícias com link direto para a matéria original.
                </p>
              </div>
            );
          }

          return (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid="news-grid"
            >
              {visibleItems.map((n) => {
                const href = getNewsUrl(n);
                const ariaLabel = `Abrir matéria original: ${n.titulo}`;
                return (
                  <a
                    key={n.news_id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={ariaLabel}
                    data-testid={`news-card-${n.news_id}`}
                    className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-2.5 min-w-0 transition cursor-pointer hover:shadow-md hover:border-slate-300 hover:-translate-y-px focus:outline-none focus:ring-2 focus:ring-[#C5A028]/40 no-underline text-inherit"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded text-white truncate max-w-[180px]"
                          style={{ background: VETOR_COLORS[n.vetor_curto] || "#0F1B2D" }}
                          title={n.vetor_completo}
                        >
                          {n.vetor_curto}
                        </span>
                        {n.is_mock && (
                          <span
                            className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                            title="Conteúdo demonstrativo"
                            data-testid="news-demo-pill"
                          >
                            Demonstração
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">
                        {relativeTime(n.publicado_em)}
                      </span>
                    </div>
                    <h3 className="font-semibold text-[15px] text-[#0F1B2D] leading-snug line-clamp-3">
                      {n.titulo}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed">{n.resumo}</p>
                    <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-slate-100">
                      <div
                        className="text-[13px] text-slate-500 min-w-0 truncate"
                        title={`${n.fonte} · ${n.pais}`}
                      >
                        <span className="font-medium text-slate-700">{n.fonte}</span>
                        <span className="text-slate-400"> · {n.pais}</span>
                      </div>
                      <span
                        onClick={(e) => e.stopPropagation()}
                        title="Abrir matéria original em nova aba"
                        className="text-[13px] text-[#C5A028] hover:text-[#8C6F1A] inline-flex items-center gap-1 shrink-0 ml-2 font-semibold pointer-events-none"
                        data-testid={`news-link-${n.news_id}`}
                      >
                        Fonte <ExternalLink size={11} aria-hidden="true" />
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          );
        })()}

        {loading && !data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {["s1", "s2", "s3", "s4", "s5", "s6"].map((id) => (
              <div key={id} className="h-36 bg-slate-50 border border-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        )}

        {data?.disclaimer && (
          <p className="text-xs text-slate-500 mt-6 italic leading-relaxed max-w-3xl">
            {data.disclaimer}
          </p>
        )}
      </div>
    </section>
  );
}
